import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Pencil, Plus, Copy } from 'lucide-react';
import AuthRequired from '@/components/auth/AuthRequired';
import AdminLayout from '@/components/admin/AdminLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from '@/components/ui/badge';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  publish_date: string;
  tags: string[];
  author: string;
}

const AdminBlogList = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process the posts to ensure all fields are properly formatted
      const processedPosts = (data || []).map(post => ({
        ...post,
        publish_date: post.publish_date || post.created_at,
        tags: Array.isArray(post.tags) ? post.tags : [],
        author: post.author || 'Admin',
        excerpt: post.excerpt || 'No excerpt available'
      }));

      setPosts(processedPosts);
    } catch (error: any) {
      console.error('Error fetching posts:', error);
      toast({
        title: 'Erro ao carregar posts',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!postToDelete) return;
    
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postToDelete);

      if (error) throw error;
      
      setPosts(posts.filter(post => post.id !== postToDelete));
      toast({
        title: 'Post excluído',
        description: 'O post foi removido com sucesso',
      });

      // Refresh the posts list after deletion
      fetchPosts();
    } catch (error: any) {
      console.error('Error deleting post:', error);
      toast({
        title: 'Erro ao excluir post',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setPostToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Data inválida';
    }
  };

  return (
    <AuthRequired>
      <AdminLayout>
        <div className="container mx-auto py-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold">Gerenciar Blog Posts</h1>
            <Button 
              onClick={() => navigate('/admin/blog/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus size={16} className="mr-2" />
              Novo Post
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : posts.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <p className="text-muted-foreground">Nenhum post encontrado</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => navigate('/admin/blog/new')}
                >
                  Criar seu primeiro post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6">
              {posts.map((post) => (
                <Card key={post.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-xl">{post.title}</CardTitle>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                          onClick={() => navigate(`/admin/blog/edit/${post.id}`)}
                        >
                          <Pencil size={16} className="text-blue-600" />
                        </Button>
                        
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300"
                          onClick={() => navigate(`/admin/blog/clone/${post.id}`)}
                        >
                          <Copy size={16} className="text-green-600" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="icon"
                              className="bg-red-50 border-red-200 hover:bg-red-100 hover:border-red-300"
                              onClick={() => setPostToDelete(post.id)}
                            >
                              <Trash2 size={16} className="text-red-600" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta ação não pode ser desfeita. Isso excluirá permanentemente o post "{post.title}".
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={handleDelete}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-2">{post.excerpt}</p>
                    <div className="flex items-center text-sm text-muted-foreground space-x-4">
                      <span>Autor: {post.author}</span>
                      <span>Data: {formatDate(post.publish_date)}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {post.tags && post.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Link 
                      to={`/blog/${post.slug}`} 
                      className="text-sm text-primary hover:underline"
                      target="_blank"
                    >
                      Ver no site
                    </Link>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </AuthRequired>
  );
};

export default AdminBlogList;
